import { Navigate, useLocation, useParams } from 'react-router';
import { lazy, Suspense } from 'react';

import { ProtectedRoute } from '@/routes/ProtectedRoute';
import {
  isV2FinanceLedgerEnabled,
  isV2ProtoRoutesEnabled,
} from '@/config/featureFlags';
import { buildAdminUsersRedirectPath, type AdminUsersRedirectMode } from './adminUsersRedirect';
import { V2AdminSettingsRedirect } from './adminSettingsRedirect';
import { V2_FINANCE_ROUTE_PATHS } from './financeRouteRegistry';
import { V2CriticalRoleRoute } from './criticalRoleRoutePolicy';
import { SETTINGS_ROUTE_ROLES } from '../pages/settings/settingsRegistry';
import { ProcurementProtectedRoute } from '../pages/ProcurementProtectedRoute';

export type V2AccountingDashboardVariant = 'ledger' | 'legacy';

// FLOW[51286]: pure selector keeps the default-on rollout and rollback testable.
export function resolveV2AccountingDashboardVariant(
  env: Record<string, unknown> = import.meta.env,
): V2AccountingDashboardVariant {
  return isV2FinanceLedgerEnabled(env) ? 'ledger' : 'legacy';
}

function LazyFallback() {
  return (
    <div data-testid="v2-route-loading" style={{ marginTop: 14 }}>
      <div className="card">
        <div className="bd">Загрузка…</div>
      </div>
    </div>
  );
}

const lazyNamedPage = <T extends Record<string, unknown>>(loader: () => Promise<T>, exportName: keyof T) =>
  lazy(async () => {
    const module = await loader();
    return { default: module[exportName] as React.ComponentType };
  });

const lazyPageElement = (
  loader: Parameters<typeof lazyNamedPage>[0],
  exportName: Parameters<typeof lazyNamedPage>[1],
) => {
  const Page = lazyNamedPage(loader, exportName);
  return (
    <Suspense fallback={<LazyFallback />}>
      <Page />
    </Suspense>
  );
};

const V2DashboardPage = lazyPageElement(() => import('../pages/V2DashboardPage'), 'V2DashboardPage');
const V2WorkdayPage = lazyPageElement(() => import('../pages/V2WorkdayPage'), 'V2WorkdayPage');
const V2OrdersIndexPage = lazyPageElement(() => import('../pages/V2OrdersIndexPage'), 'V2OrdersIndexPage');
const V2OrderCreatePage = lazyPageElement(() => import('../pages/V2OrderCreatePage'), 'V2OrderCreatePage');
const V2OrderCreateSuccessPage = lazyPageElement(
  () => import('../pages/V2OrderCreateSuccessPage'),
  'V2OrderCreateSuccessPage',
);
const V2OrderCardPage = lazyPageElement(() => import('../pages/V2OrderCardPage'), 'V2OrderCardPage');
const V2OrderEditPage = lazyPageElement(() => import('../pages/V2OrderEditPage'), 'V2OrderEditPage');
const V2OrderSubcontractingPage = lazyPageElement(
  () => import('../pages/V2OrderSubcontractingPage'),
  'V2OrderSubcontractingPage',
);
const V2ProductionPage = lazyPageElement(() => import('../pages/V2ProductionPage'), 'V2ProductionPage');
// FLOW[50361]: capacity is shared with production; economics remains backend role-projected.
const V2GrowthCapacityPage = lazyPageElement(
  () => import('../pages/V2GrowthCapacityPage'),
  'V2GrowthCapacityPage',
);
const V2ProductionTaskDryRunPage = lazyPageElement(
  () => import('../pages/V2ProductionTaskDryRunPage'),
  'V2ProductionTaskDryRunPage',
);
const V2ProductionTaskCreateReviewPage = lazyPageElement(
  () => import('../pages/V2ProductionTaskCreateReviewPage'),
  'V2ProductionTaskCreateReviewPage',
);
const V2ProcessCenterPage = lazyPageElement(
  () => import('../pages/V2ProcessCenterPage'),
  'V2ProcessCenterPage',
);
const V2ProductionTaskCancelReviewPage = lazyPageElement(
  () => import('../pages/V2ProductionTaskCancelReviewPage'),
  'V2ProductionTaskCancelReviewPage',
);
const V2StockPage = lazyPageElement(() => import('../pages/V2StockPage'), 'V2StockPage');
const V2StockLocationsPage = lazyPageElement(
  () => import('../pages/V2StockLocationsPage'),
  'V2StockLocationsPage',
);
const V2StockPurchaseOrdersPage = lazyPageElement(
  () => import('../pages/V2StockPurchaseOrdersPage'),
  'V2StockPurchaseOrdersPage',
);
const V2StockIncomingLotsPage = lazyPageElement(
  () => import('../pages/V2StockIncomingLotsPage'),
  'V2StockIncomingLotsPage',
);
const V2AccountingDashboardPage = lazyPageElement(
  () => import('../pages/V2AccountingDashboardPage'),
  'V2AccountingDashboardPage',
);
const V2AccountingLedgerDashboardPage = lazyPageElement(
  () => import('../pages/V2AccountingLedgerDashboardPage'),
  'V2AccountingLedgerDashboardPage',
);
// FLOW[51286]: default-on ledger with a no-route-change legacy rollback.
const V2AccountingHomePage =
  resolveV2AccountingDashboardVariant() === 'ledger'
    ? V2AccountingLedgerDashboardPage
    : V2AccountingDashboardPage;
const V2Personal1CPage = lazyPageElement(
  () => import('../pages/V2Personal1CPage'),
  'V2Personal1CPage',
);
const V2AccountingEdoRegisterPage = lazyPageElement(
  () => import('../pages/V2AccountingEdoRegisterPage'),
  'V2AccountingEdoRegisterPage',
);
const V2AccountingReconciliationCockpitPage = lazyPageElement(
  () => import('../pages/V2AccountingReconciliationCockpitPage'),
  'V2AccountingReconciliationCockpitPage',
);
const V2AccountingFinanceWorkspacePage = lazyPageElement(
  () => import('../pages/V2AccountingFinanceWorkspacePage'),
  'V2AccountingFinanceWorkspacePage',
);
const V2AccountingPaymentMatchPage = lazyPageElement(
  () => import('../pages/V2AccountingPaymentMatchPage'),
  'V2AccountingPaymentMatchPage',
);
const V2AccountingPayablesPage = lazyPageElement(
  () => import('../pages/V2AccountingPayablesPage'),
  'V2AccountingPayablesPage',
);
const V2AccountingInvoiceCreatePage = lazyPageElement(
  () => import('../pages/V2AccountingInvoiceCreatePage'),
  'V2AccountingInvoiceCreatePage',
);
const V2AccountingInvoiceSendPage = lazyPageElement(
  () => import('../pages/V2AccountingInvoiceSendPage'),
  'V2AccountingInvoiceSendPage',
);
const V2AccountingClosingDocPage = lazyPageElement(
  () => import('../pages/V2AccountingClosingDocPage'),
  'V2AccountingClosingDocPage',
);
// FLOW[46300]: реестр «Закрывающие документы из 1С» (отдельно от черновика УПД)
const V2ClosingDocsRegistryPage = lazyPageElement(
  () => import('../pages/V2ClosingDocsRegistryPage'),
  'V2ClosingDocsRegistryPage',
);
const V2AccountingBankReconcilePage = lazyPageElement(
  () => import('../pages/V2AccountingBankReconcilePage'),
  'V2AccountingBankReconcilePage',
);
const V2ManagementPnlPage = lazyPageElement(
  () => import('../pages/V2ManagementPnlPage'),
  'V2ManagementPnlPage',
);
// FLOW[50056]: drill-down «Аналитика по партнёрам» (карточка «Партнёры» на дашборде руководителя)
const V2PartnerAnalyticsPage = lazyPageElement(
  () => import('../pages/V2PartnerAnalyticsPage'),
  'V2PartnerAnalyticsPage',
);
const V2CostBomPage = lazyPageElement(
  () => import('../pages/V2CostBomPage'),
  'V2CostBomPage',
);
// FLOW[47564]: реестр платёжных поручений (§17)
const V2PaymentOrdersPage = lazyPageElement(
  () => import('../pages/V2PaymentOrdersPage'),
  'V2PaymentOrdersPage',
);

// FLOW[49640]: экран руководителя производства «Счета партнёрам» (накопитель заявок)
const V2PartnerBatchesPage = lazyPageElement(
  () => import('../pages/V2PartnerBatchesPage'),
  'V2PartnerBatchesPage',
);
const V2AccountingQuarterlyProfitabilityPage = lazyPageElement(
  () => import('../pages/V2AccountingQuarterlyProfitabilityPage'),
  'V2AccountingQuarterlyProfitabilityPage',
);
const V2AccountingMonthlyFinancePage = lazyPageElement(
  () => import('../pages/V2AccountingMonthlyFinancePage'),
  'V2AccountingMonthlyFinancePage',
);
const V2AccountingFixedCostsPage = lazyPageElement(
  () => import('../pages/V2AccountingFixedCostsPage'),
  'V2AccountingFixedCostsPage',
);
const V2AccountingInputVatCleanupPage = lazyPageElement(
  () => import('../pages/V2AccountingInputVatCleanupPage'),
  'V2AccountingInputVatCleanupPage',
);
const V2AccountingInputVatUnallocatedPage = lazyPageElement(
  () => import('../pages/V2AccountingInputVatUnallocatedPage'),
  'V2AccountingInputVatUnallocatedPage',
);
const V2AccountingTaxObligationsPage = lazyPageElement(
  () => import('../pages/V2AccountingTaxObligationsPage'),
  'V2AccountingTaxObligationsPage',
);
const V2AccountingProfitabilitySourceExplorerPage = lazyPageElement(
  () => import('../pages/V2AccountingProfitabilitySourceExplorerPage'),
  'V2AccountingProfitabilitySourceExplorerPage',
);
const V2AccountingProcurementVatPage = lazyPageElement(
  () => import('../pages/V2AccountingProcurementVatPage'),
  'V2AccountingProcurementVatPage',
);
const V2IndustryErpPage = lazyPageElement(
  () => import('../pages/V2IndustryErpPage'),
  'V2IndustryErpPage',
);
const V2ExecutiveDashboardPage = lazyPageElement(
  () => import('../pages/V2ExecutiveDashboardPage'),
  'V2ExecutiveDashboardPage',
);
const V2ExecutiveCommunicationsPage = lazyPageElement(
  () => import('../pages/V2ExecutiveCommunicationsPage'),
  'V2ExecutiveCommunicationsPage',
);
const V2PipelinePage = lazyPageElement(() => import('../pages/V2PipelinePage'), 'V2PipelinePage');
const V2ClientsPage = lazyPageElement(() => import('../pages/V2ClientsPage'), 'V2ClientsPage');
const V2ClientDetailPage = lazyPageElement(() => import('../pages/V2ClientDetailPage'), 'V2ClientDetailPage');
const V2ClientFormPage = lazyPageElement(() => import('../pages/V2ClientFormPage'), 'V2ClientFormPage');
const V2QuotesPage = lazyPageElement(() => import('../pages/V2QuotesPage'), 'V2QuotesPage');
const V2QuoteDetailPage = lazyPageElement(() => import('../pages/V2QuoteDetailPage'), 'V2QuoteDetailPage');
const V2PartnerSheetPage = lazyPageElement(() => import('../pages/V2PartnerSheetPage'), 'V2PartnerSheetPage');
const V2MessageCenterPage = lazyPageElement(() => import('../pages/V2MessageCenterPage'), 'V2MessageCenterPage');
const V2TasksPage = lazyPageElement(() => import('../pages/V2TasksPage'), 'V2TasksPage');
const V2AIAssistantPage = lazyPageElement(() => import('../pages/V2AIAssistantPage'), 'V2AIAssistantPage');
const V2TrainingReviewPage = lazyPageElement(
  () => import('../pages/V2TrainingReviewPage'),
  'V2TrainingReviewPage',
);
const V2ImprovementCenterPage = lazyPageElement(
  () => import('../pages/V2ImprovementCenterPage'),
  'V2ImprovementCenterPage',
);
const V2KnowledgePage = lazyPageElement(() => import('../pages/V2KnowledgePage'), 'V2KnowledgePage');
// FLOW[49083]: isolated Digital Twin QA control plane, admin-only.
const V2QaDigitalTwinPage = lazyPageElement(
  () => import('../pages/V2QaDigitalTwinPage'),
  'V2QaDigitalTwinPage',
);
const V2CatalogPage = lazyPageElement(() => import('../pages/V2CatalogPage'), 'V2CatalogPage');
const V2SalesAssortmentReviewPage = lazyPageElement(
  () => import('../pages/V2SalesAssortmentReviewPage'),
  'V2SalesAssortmentReviewPage',
);
// FLOW[50802]: fail-closed owner ledger for site-chat prices and production terms.
const V2PublicOfferPublicationPage = lazyPageElement(
  () => import('../pages/V2PublicOfferPublicationPage'),
  'V2PublicOfferPublicationPage',
);
const V2ArtworkRequirementsPage = lazyPageElement(
  () => import('../pages/V2ArtworkRequirementsPage'),
  'V2ArtworkRequirementsPage',
);
const V2ExternalCatalogImportsPage = lazyPageElement(
  () => import('../pages/V2ExternalCatalogImportsPage'),
  'V2ExternalCatalogImportsPage',
);
const V2SiteMappingsPage = lazyPageElement(
  () => import('../pages/V2SiteMappingsPage'),
  'V2SiteMappingsPage',
);
// FLOW[47576]: редактор розничных цен на материалы (₽/м²) — admin/executive.
const V2MaterialPricesPage = lazyPageElement(
  () => import('../pages/V2MaterialPricesPage'),
  'V2MaterialPricesPage',
);
const V2NotificationsPage = lazyPageElement(() => import('../pages/V2NotificationsPage'), 'V2NotificationsPage');
const V2ProfilePage = lazyPageElement(() => import('../pages/V2ProfilePage'), 'V2ProfilePage');
// FLOW[46035]: единый центр настроек — layout с меню категорий + секции по :sectionId
const V2SettingsLayout = lazyPageElement(
  () => import('../pages/settings/V2SettingsLayout'),
  'V2SettingsLayout',
);
const V2SettingsSectionHost = lazyPageElement(
  () => import('../pages/settings/V2SettingsSectionHost'),
  'V2SettingsSectionHost',
);
const V2SettingsIndexRedirect = lazyPageElement(
  () => import('../pages/settings/V2SettingsSectionHost'),
  'V2SettingsIndexRedirect',
);
const V2SubcontractingPage = lazyPageElement(() => import('../pages/V2SubcontractingPage'), 'V2SubcontractingPage');
const V2LogisticsPage = lazyPageElement(() => import('../pages/V2LogisticsPage'), 'V2LogisticsPage');
const V2MediaPage = lazyPageElement(() => import('../pages/V2MediaPage'), 'V2MediaPage');
const V2DiskPage = lazyPageElement(() => import('../pages/V2DiskPage'), 'V2DiskPage');
const V2DesignTasksInboxPage = lazyPageElement(
  () => import('../pages/V2DesignTasksInboxPage'),
  'V2DesignTasksInboxPage',
);
const V2PrepressPage = lazyPageElement(() => import('../pages/V2PrepressPage'), 'V2PrepressPage');
const V2PrepressQueuePage = lazyPageElement(
  () => import('../pages/V2PrepressQueuePage'),
  'V2PrepressQueuePage',
);
const V2PrepressEmailIntakesPage = lazyPageElement(
  () => import('../pages/V2PrepressEmailIntakesPage'),
  'V2PrepressEmailIntakesPage',
);
const V2PrepressBetaInvitesPage = lazyPageElement(
  () => import('../pages/V2PrepressBetaInvitesPage'),
  'V2PrepressBetaInvitesPage',
);
const V2CatalogProductPage = lazyPageElement(
  () => import('../pages/V2CatalogProductPage'),
  'V2CatalogProductPage',
);
const V2CompetitiveAnalysisPage = lazyPageElement(
  () => import('../pages/V2CompetitiveAnalysisPage'),
  'V2CompetitiveAnalysisPage',
);
// FLOW[46113]: Зарплаты 2.0 — закрытие счетов в зарплату
const V2SalaryClosuresPage = lazyPageElement(
  () => import('../pages/V2SalaryClosuresPage'),
  'V2SalaryClosuresPage',
);
const V2FleetLivePage = lazyPageElement(
  () => import('../pages/V2FleetLivePage'),
  'V2FleetLivePage',
);
const V2FleetReplayPage = lazyPageElement(
  () => import('../pages/V2FleetReplayPage'),
  'V2FleetReplayPage',
);
const V2AlignmentQueuePage = lazyPageElement(
  () => import('../pages/V2AlignmentQueuePage'),
  'V2AlignmentQueuePage',
);

// Прототипы для согласования (/v2/proto/*): код сохранён полностью, в маршруты попадает
// только за флагом isV2ProtoRoutesEnabled (B2 Operator UX 2.0, roadmap UX-P-04).
export const buildV2ProtoRoutes = () => {
  const ProtoIndexPage = lazyPageElement(() => import('@/prototypes/ProtoIndexPage'), 'ProtoIndexPage');
  const ProtoMorningDashboard = lazyPageElement(
    () => import('@/prototypes/ProtoMorningDashboard'),
    'ProtoMorningDashboard',
  );
  const ProtoOrderCard = lazyPageElement(() => import('@/prototypes/ProtoOrderCard'), 'ProtoOrderCard');
  const ProtoOrdersIndexPage = lazyPageElement(() => import('@/prototypes/ProtoOrdersIndexPage'), 'ProtoOrdersIndexPage');
  const ProtoTasksPage = lazyPageElement(() => import('@/prototypes/ProtoTasksPage'), 'ProtoTasksPage');
  const ProtoOrderWizard = lazyPageElement(() => import('@/prototypes/ProtoOrderWizard'), 'ProtoOrderWizard');
  const ProtoPipelinePage = lazyPageElement(() => import('@/prototypes/ProtoPipelinePage'), 'ProtoPipelinePage');
  const ProtoProductionBoard = lazyPageElement(() => import('@/prototypes/ProtoProductionBoard'), 'ProtoProductionBoard');
  const ProtoMobileKPI = lazyPageElement(() => import('@/prototypes/ProtoMobileKPI'), 'ProtoMobileKPI');
  const ProtoClientCard = lazyPageElement(() => import('@/prototypes/ProtoClientCard'), 'ProtoClientCard');
  const ProtoAccountingDesk = lazyPageElement(() => import('@/prototypes/ProtoAccountingDesk'), 'ProtoAccountingDesk');
  const ProtoQuotesPage = lazyPageElement(() => import('@/prototypes/ProtoQuotesPage'), 'ProtoQuotesPage');
  const ProtoStockPage = lazyPageElement(() => import('@/prototypes/ProtoStockPage'), 'ProtoStockPage');
  const ProtoLogisticsPage = lazyPageElement(() => import('@/prototypes/ProtoLogisticsPage'), 'ProtoLogisticsPage');
  const ProtoNotificationsPage = lazyPageElement(() => import('@/prototypes/ProtoMiscPages'), 'ProtoNotificationsPage');
  const ProtoSettingsPage = lazyPageElement(() => import('@/prototypes/ProtoMiscPages'), 'ProtoSettingsPage');
  const ProtoMessageCenter = lazyPageElement(() => import('@/prototypes/ProtoMessageCenter'), 'ProtoMessageCenter');
  const ProtoSubcontractingPage = lazyPageElement(() => import('@/prototypes/ProtoSubcontractingPage'), 'ProtoSubcontractingPage');
  const ProtoCatalogPage = lazyPageElement(() => import('@/prototypes/ProtoSecondaryPages'), 'ProtoCatalogPage');
  const ProtoDesignTasksPage = lazyPageElement(() => import('@/prototypes/ProtoSecondaryPages'), 'ProtoDesignTasksPage');
  const ProtoPrepressPage = lazyPageElement(() => import('@/prototypes/ProtoSecondaryPages'), 'ProtoPrepressPage');
  const ProtoMediaPage = lazyPageElement(() => import('@/prototypes/ProtoSecondaryPages'), 'ProtoMediaPage');
  const ProtoCompetitiveAnalysis = lazyPageElement(() => import('@/prototypes/ProtoSecondaryPages'), 'ProtoCompetitiveAnalysis');
  const ProtoAdminPage = lazyPageElement(() => import('@/prototypes/ProtoAdminPages'), 'ProtoAdminPage');
  const ProtoExecutiveDashboard = lazyPageElement(() => import('@/prototypes/ProtoAdminPages'), 'ProtoExecutiveDashboard');
  const ProtoProfilePage = lazyPageElement(() => import('@/prototypes/ProtoAdminPages'), 'ProtoProfilePage');

  return [
    { path: 'proto', element: ProtoIndexPage },
    { path: 'proto/morning-dashboard', element: ProtoMorningDashboard },
    { path: 'proto/order-card', element: ProtoOrderCard },
    { path: 'proto/orders', element: ProtoOrdersIndexPage },
    { path: 'proto/tasks', element: ProtoTasksPage },
    { path: 'proto/order-wizard', element: ProtoOrderWizard },
    { path: 'proto/pipeline', element: ProtoPipelinePage },
    { path: 'proto/production', element: ProtoProductionBoard },
    { path: 'proto/mobile-kpi', element: ProtoMobileKPI },
    { path: 'proto/client-card', element: ProtoClientCard },
    { path: 'proto/accounting', element: ProtoAccountingDesk },
    { path: 'proto/quotes', element: ProtoQuotesPage },
    { path: 'proto/stock', element: ProtoStockPage },
    { path: 'proto/logistics', element: ProtoLogisticsPage },
    { path: 'proto/notifications', element: ProtoNotificationsPage },
    { path: 'proto/settings', element: ProtoSettingsPage },
    { path: 'proto/messages', element: ProtoMessageCenter },
    { path: 'proto/subcontracting', element: ProtoSubcontractingPage },
    { path: 'proto/catalog', element: ProtoCatalogPage },
    { path: 'proto/design-tasks', element: ProtoDesignTasksPage },
    { path: 'proto/prepress', element: ProtoPrepressPage },
    { path: 'proto/media', element: ProtoMediaPage },
    { path: 'proto/competitive', element: ProtoCompetitiveAnalysis },
    { path: 'proto/admin', element: ProtoAdminPage },
    { path: 'proto/executive', element: ProtoExecutiveDashboard },
    { path: 'proto/profile', element: ProtoProfilePage },
  ];
};

const RegistryPage = lazyPageElement(() => import('@/pages/catalog/RegistryPage'), 'RegistryPage');

function V2AdminUsersRedirect({ mode }: { mode: AdminUsersRedirectMode }) {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  return <Navigate to={buildAdminUsersRedirectPath(mode, id, location.search)} replace />;
}

export const v2Routes = [
  {
    index: true,
    element: <Navigate to="/v2/dashboard" replace />,
  },
  {
    path: 'dashboard',
    element: V2DashboardPage,
  },
  {
    path: 'workday',
    element: (
      <ProtectedRoute
        allowedRoles={[
          'admin',
          'manager',
          'sales_manager',
          'production',
          'operator',
          'accountant',
          'executive',
        ]}
      >
        {V2WorkdayPage}
      </ProtectedRoute>
    ),
  },
  {
    path: 'pipeline',
    element: (
      <V2CriticalRoleRoute policy="sales">
        {V2PipelinePage}
      </V2CriticalRoleRoute>
    ),
  },
  {
    path: 'competitive-analysis',
    element: V2CompetitiveAnalysisPage,
  },
  {
    // FLOW[46113]: Зарплаты 2.0 — закрытие счетов в зарплату
    path: 'salary-closures',
    element: (
      <ProtectedRoute allowedRoles={['admin', 'sales_manager', 'executive']}>
        {V2SalaryClosuresPage}
      </ProtectedRoute>
    ),
  },
  {
    path: 'clients',
    element: (
      <V2CriticalRoleRoute policy="sales">
        {V2ClientsPage}
      </V2CriticalRoleRoute>
    ),
  },
  {
    path: 'clients/new',
    element: (
      <V2CriticalRoleRoute policy="sales">
        {V2ClientFormPage}
      </V2CriticalRoleRoute>
    ),
  },
  {
    path: 'clients/:id',
    element: (
      <V2CriticalRoleRoute policy="sales">
        {V2ClientDetailPage}
      </V2CriticalRoleRoute>
    ),
  },
  {
    path: 'clients/:id/edit',
    element: (
      <V2CriticalRoleRoute policy="sales">
        {V2ClientFormPage}
      </V2CriticalRoleRoute>
    ),
  },
  {
    path: 'quotes',
    element: (
      <V2CriticalRoleRoute policy="sales">
        {V2QuotesPage}
      </V2CriticalRoleRoute>
    ),
  },
  {
    path: 'quotes/:id',
    element: (
      <V2CriticalRoleRoute policy="sales">
        {V2QuoteDetailPage}
      </V2CriticalRoleRoute>
    ),
  },
  {
    path: 'partner-sheet',
    element: (
      <V2CriticalRoleRoute policy="partnerSheet">
        {V2PartnerSheetPage}
      </V2CriticalRoleRoute>
    ),
  },
  {
    path: 'messages',
    element: V2MessageCenterPage,
  },
  {
    path: 'tasks',
    element: V2TasksPage,
  },
  {
    path: 'message-center',
    element: V2MessageCenterPage,
  },
  {
    // AI-Ops пульт (E-53): страница только для админа/внедренца; обычные роли
    // открывают ассистента дровером из меню (B3, решение владельца).
    path: 'assistant',
    element: (
      <ProtectedRoute allowedRoles={['admin']}>
        {V2AIAssistantPage}
      </ProtectedRoute>
    ),
  },
  {
    path: 'assistant/training-review',
    element: (
      <ProtectedRoute allowedRoles={['admin']}>
        {V2TrainingReviewPage}
      </ProtectedRoute>
    ),
  },
  {
    path: 'improvement-center',
    element: (
      <ProtectedRoute allowedRoles={['admin']}>
        {V2ImprovementCenterPage}
      </ProtectedRoute>
    ),
  },
  {
    path: 'knowledge',
    element: (
      <ProtectedRoute allowedRoles={['admin']}>
        {V2KnowledgePage}
      </ProtectedRoute>
    ),
  },
  {
    path: 'qa/digital-twin',
    element: (
      <ProtectedRoute allowedRoles={['admin']}>
        {V2QaDigitalTwinPage}
      </ProtectedRoute>
    ),
  },
  // FLOW[31958]: Alias route for messenger center
  {
    path: 'messenger',
    element: V2MessageCenterPage,
  },
  {
    path: 'orders',
    element: (
      <V2CriticalRoleRoute policy="companyOrders">
        {V2OrdersIndexPage}
      </V2CriticalRoleRoute>
    ),
  },
  {
    path: 'orders/new',
    element: (
      <V2CriticalRoleRoute policy="companyOrders">
        {V2OrderCreatePage}
      </V2CriticalRoleRoute>
    ),
  },
  {
    path: 'orders/:id/created',
    element: (
      <V2CriticalRoleRoute policy="companyOrders">
        {V2OrderCreateSuccessPage}
      </V2CriticalRoleRoute>
    ),
  },
  {
    path: 'orders/:id',
    element: (
      <V2CriticalRoleRoute policy="companyOrders">
        {V2OrderCardPage}
      </V2CriticalRoleRoute>
    ),
  },
  {
    path: 'orders/:id/edit',
    element: (
      <V2CriticalRoleRoute policy="companyOrders">
        {V2OrderEditPage}
      </V2CriticalRoleRoute>
    ),
  },
  {
    path: 'orders/:id/subcontracting',
    element: (
      <V2CriticalRoleRoute policy="companyOrders">
        {V2OrderSubcontractingPage}
      </V2CriticalRoleRoute>
    ),
  },
  {
    path: 'production',
    element: (
      <V2CriticalRoleRoute policy="production">
        {V2ProductionPage}
      </V2CriticalRoleRoute>
    ),
  },
  {
    path: 'growth-capacity',
    element: (
      <V2CriticalRoleRoute policy="production">
        {V2GrowthCapacityPage}
      </V2CriticalRoleRoute>
    ),
  },
  {
    path: 'fleet/live',
    element: (
      <ProtectedRoute allowedRoles={['admin', 'production', 'executive']}>
        {V2FleetLivePage}
      </ProtectedRoute>
    ),
  },
  {
    path: 'fleet/replay',
    element: (
      <ProtectedRoute allowedRoles={['admin', 'production', 'executive']}>
        {V2FleetReplayPage}
      </ProtectedRoute>
    ),
  },
  {
    path: 'alignment',
    element: (
      <ProtectedRoute allowedRoles={['admin', 'production', 'executive']}>
        {V2AlignmentQueuePage}
      </ProtectedRoute>
    ),
  },
  {
    // B84: read-only production task-create dry-run preview (no task creation / no writes)
    path: 'production/task-dry-run',
    element: (
      <V2CriticalRoleRoute policy="production">
        {V2ProductionTaskDryRunPage}
      </V2CriticalRoleRoute>
    ),
  },
  {
    // B87: production task-create review + handoff (creates PLANNED tasks via the ai-assistant handoff;
    // the browser never calls the production confirm endpoint directly)
    path: 'production/task-create-review',
    element: (
      <V2CriticalRoleRoute policy="production">
        {V2ProductionTaskCreateReviewPage}
      </V2CriticalRoleRoute>
    ),
  },
  {
    // B88: process center v0 (READ-ONLY) — process definitions, task center (created-via-AI), instances.
    // No start/complete/execute surfaces; the browser only reads the process bounded-context read model.
    path: 'production/process-center',
    element: (
      <V2CriticalRoleRoute policy="production">
        {V2ProcessCenterPage}
      </V2CriticalRoleRoute>
    ),
  },
  {
    // B89: task-cancel COMPENSATION review (cancel = state transition of planned tasks, never a delete; the
    // browser talks only to the ai-assistant compensation handoff, never the production confirm endpoint)
    path: 'production/task-cancel-review',
    element: (
      <V2CriticalRoleRoute policy="production">
        {V2ProductionTaskCancelReviewPage}
      </V2CriticalRoleRoute>
    ),
  },
  {
    path: 'stock',
    element: (
      <V2CriticalRoleRoute policy="stock">
        {V2StockPage}
      </V2CriticalRoleRoute>
    ),
  },
  {
    path: 'stock/locations',
    element: (
      <V2CriticalRoleRoute policy="stock">
        {V2StockLocationsPage}
      </V2CriticalRoleRoute>
    ),
  },
  {
    path: 'stock/purchase-orders',
    element: (
      <ProcurementProtectedRoute>
        {V2StockPurchaseOrdersPage}
      </ProcurementProtectedRoute>
    ),
  },
  {
    path: 'stock/incoming-lots',
    element: (
      <ProcurementProtectedRoute>
        {V2StockIncomingLotsPage}
      </ProcurementProtectedRoute>
    ),
  },
  {
    path: 'industry-erp',
    element: (
      <ProtectedRoute
        allowedRoles={[
          'admin',
          'manager',
          'operator',
          'sales_manager',
          'production',
          'accountant',
          'executive',
        ]}
      >
        {V2IndustryErpPage}
      </ProtectedRoute>
    ),
  },
  {
    path: 'logistics',
    element: V2LogisticsPage,
  },
  {
    path: 'subcontracting',
    element: V2SubcontractingPage,
  },
  {
    path: 'media',
    element: V2MediaPage,
  },
  {
    path: 'disk',
    element: V2DiskPage,
  },
  {
    path: 'catalog/artwork-requirements',
    element: (
      <ProtectedRoute allowedRoles={['admin', 'production', 'executive']}>
        {V2ArtworkRequirementsPage}
      </ProtectedRoute>
    ),
  },
  {
    path: 'catalog',
    element: V2CatalogPage,
  },
  {
    // FLOW[50424]: only catalog administrators may publish manager hints.
    path: 'catalog/sales-assortment',
    element: (
      <ProtectedRoute allowedRoles={['admin']}>
        {V2SalesAssortmentReviewPage}
      </ProtectedRoute>
    ),
  },
  {
    // FLOW[50802]: PublicOffer management is globally scoped only for admin roles.
    path: 'catalog/public-offers',
    element: (
      <ProtectedRoute allowedRoles={['admin']}>
        {V2PublicOfferPublicationPage}
      </ProtectedRoute>
    ),
  },
  {
    path: 'catalog/external-imports',
    element: V2ExternalCatalogImportsPage,
  },
  {
    // FLOW[51297]: commercial mapping workbench is permission-gated and never exposes production writes.
    path: 'catalog/site-mappings',
    element: (
      <ProtectedRoute allowedRoles={['admin', 'sales_manager', 'executive']}>
        <ProtectedRoute
          anyPermission={[
            'catalog:read',
            'catalog:create',
            'catalog:update',
            'catalog:delete',
            'catalog:write',
            'catalog:admin',
          ]}
        >
          {V2SiteMappingsPage}
        </ProtectedRoute>
      </ProtectedRoute>
    ),
  },
  {
    // FLOW[47576]: редактор розничных цен на материалы (₽/м²)
    path: 'catalog/material-prices',
    element: (
      <ProtectedRoute allowedRoles={['admin', 'executive']}>
        {V2MaterialPricesPage}
      </ProtectedRoute>
    ),
  },
  {
    path: 'catalog/registry',
    element: RegistryPage,
  },
  {
    path: 'catalog/product/:id',
    element: V2CatalogProductPage,
  },
  {
    path: 'design-tasks',
    element: V2DesignTasksInboxPage,
  },
  {
    path: 'prepress',
    element: (
      <V2CriticalRoleRoute policy="prepress">
        {V2PrepressPage}
      </V2CriticalRoleRoute>
    ),
  },
  {
    path: 'prepress/queue',
    element: (
      <V2CriticalRoleRoute policy="prepressQueue">
        {V2PrepressQueuePage}
      </V2CriticalRoleRoute>
    ),
  },
  {
    path: 'prepress/email-intakes',
    element: (
      <V2CriticalRoleRoute policy="prepress">
        {V2PrepressEmailIntakesPage}
      </V2CriticalRoleRoute>
    ),
  },
  {
    path: 'prepress/beta-invites',
    element: (
      <ProtectedRoute allowedRoles={['admin']}>
        {V2PrepressBetaInvitesPage}
      </ProtectedRoute>
    ),
  },
  {
    path: V2_FINANCE_ROUTE_PATHS.accounting,
    element: (
      <V2CriticalRoleRoute policy="financeRead">
        {V2AccountingHomePage}
      </V2CriticalRoleRoute>
    ),
  },
  {
    path: V2_FINANCE_ROUTE_PATHS.personal1c,
    element: (
      <ProtectedRoute requiredPermission="personal_finance:read">
        {V2Personal1CPage}
      </ProtectedRoute>
    ),
  },
  {
    path: V2_FINANCE_ROUTE_PATHS.financeWorkspace,
    element: (
      <ProtectedRoute allowedRoles={['admin', 'accountant', 'executive']}>
        {V2AccountingFinanceWorkspacePage}
      </ProtectedRoute>
    ),
  },
  {
    path: V2_FINANCE_ROUTE_PATHS.edoRegister,
    element: (
      <V2CriticalRoleRoute policy="financeRead">
        {V2AccountingEdoRegisterPage}
      </V2CriticalRoleRoute>
    ),
  },
  {
    path: V2_FINANCE_ROUTE_PATHS.reconciliation,
    element: (
      <V2CriticalRoleRoute policy="financeRead">
        {V2AccountingReconciliationCockpitPage}
      </V2CriticalRoleRoute>
    ),
  },
  {
    path: V2_FINANCE_ROUTE_PATHS.paymentMatch,
    element: (
      <ProtectedRoute allowedRoles={['admin', 'accountant']}>
        {V2AccountingPaymentMatchPage}
      </ProtectedRoute>
    ),
  },
  {
    path: V2_FINANCE_ROUTE_PATHS.payables,
    element: (
      <ProtectedRoute allowedRoles={['admin', 'accountant', 'executive']}>
        {V2AccountingPayablesPage}
      </ProtectedRoute>
    ),
  },
  {
    path: V2_FINANCE_ROUTE_PATHS.invoiceCreate,
    element: (
      <ProtectedRoute allowedRoles={['admin', 'accountant']}>
        {V2AccountingInvoiceCreatePage}
      </ProtectedRoute>
    ),
  },
  {
    path: V2_FINANCE_ROUTE_PATHS.invoiceSend,
    element: (
      <ProtectedRoute allowedRoles={['admin', 'accountant', 'executive']}>
        {V2AccountingInvoiceSendPage}
      </ProtectedRoute>
    ),
  },
  {
    path: V2_FINANCE_ROUTE_PATHS.closingDoc,
    element: (
      <ProtectedRoute allowedRoles={['admin', 'accountant', 'executive']}>
        {V2AccountingClosingDocPage}
      </ProtectedRoute>
    ),
  },
  {
    // FLOW[46300]: реестр закрывающих из 1С (Реализация/Акт/УПД) + привязка к счетам
    path: 'accounting/closing-docs-registry',
    element: (
      <ProtectedRoute allowedRoles={['admin', 'accountant', 'executive']}>
        {V2ClosingDocsRegistryPage}
      </ProtectedRoute>
    ),
  },
  {
    path: V2_FINANCE_ROUTE_PATHS.bankReconcile,
    element: (
      <ProtectedRoute allowedRoles={['admin', 'accountant']}>
        {V2AccountingBankReconcilePage}
      </ProtectedRoute>
    ),
  },
  {
    path: V2_FINANCE_ROUTE_PATHS.managementPnl,
    element: (
      <ProtectedRoute allowedRoles={['admin', 'accountant', 'executive']}>
        {V2ManagementPnlPage}
      </ProtectedRoute>
    ),
  },
  {
    path: V2_FINANCE_ROUTE_PATHS.partnerAnalytics,
    element: (
      <ProtectedRoute allowedRoles={['admin', 'accountant', 'executive']}>
        {V2PartnerAnalyticsPage}
      </ProtectedRoute>
    ),
  },
  {
    path: V2_FINANCE_ROUTE_PATHS.costBom,
    element: (
      <ProtectedRoute allowedRoles={['admin', 'accountant', 'executive']}>
        {V2CostBomPage}
      </ProtectedRoute>
    ),
  },
  {
    path: 'accounting/payment-orders',
    element: (
      <ProtectedRoute allowedRoles={['admin', 'accountant', 'executive']}>
        {V2PaymentOrdersPage}
      </ProtectedRoute>
    ),
  },
  {
    // FLOW[49640]: рабочее место руководителя производства — сюда же ходят бухгалтер
    // и исполнительный; партнёр в этот периметр не допускается (гейт на бэкенде тоже).
    path: 'accounting/partner-batches',
    element: (
      <ProtectedRoute allowedRoles={['admin', 'accountant', 'executive', 'production']}>
        {V2PartnerBatchesPage}
      </ProtectedRoute>
    ),
  },
  {
    path: V2_FINANCE_ROUTE_PATHS.quarterlyProfitability,
    // FLOW[45657]: гейт роли — как у остальных финанс-страниц (был открыт любому залогиненному)
    element: (
      <ProtectedRoute allowedRoles={['admin', 'accountant', 'executive']}>
        {V2AccountingQuarterlyProfitabilityPage}
      </ProtectedRoute>
    ),
  },
  {
    path: V2_FINANCE_ROUTE_PATHS.monthlyFinance,
    element: (
      <ProtectedRoute allowedRoles={['admin', 'accountant', 'executive']}>
        {V2AccountingMonthlyFinancePage}
      </ProtectedRoute>
    ),
  },
  {
    path: V2_FINANCE_ROUTE_PATHS.fixedCosts,
    element: (
      <V2CriticalRoleRoute policy="financeRead">
        {V2AccountingFixedCostsPage}
      </V2CriticalRoleRoute>
    ),
  },
  {
    path: V2_FINANCE_ROUTE_PATHS.inputVatCleanup,
    element: (
      <V2CriticalRoleRoute policy="financeRead">
        {V2AccountingInputVatCleanupPage}
      </V2CriticalRoleRoute>
    ),
  },
  {
    path: V2_FINANCE_ROUTE_PATHS.inputVatUnallocated,
    element: (
      <V2CriticalRoleRoute policy="financeRead">
        {V2AccountingInputVatUnallocatedPage}
      </V2CriticalRoleRoute>
    ),
  },
  {
    path: V2_FINANCE_ROUTE_PATHS.taxObligations,
    element: (
      <V2CriticalRoleRoute policy="financeRead">
        {V2AccountingTaxObligationsPage}
      </V2CriticalRoleRoute>
    ),
  },
  {
    path: V2_FINANCE_ROUTE_PATHS.profitabilitySourceExplorer,
    element: (
      <V2CriticalRoleRoute policy="financeRead">
        {V2AccountingProfitabilitySourceExplorerPage}
      </V2CriticalRoleRoute>
    ),
  },
  {
    path: V2_FINANCE_ROUTE_PATHS.procurementVat,
    element: (
      <V2CriticalRoleRoute policy="financeRead">
        {V2AccountingProcurementVatPage}
      </V2CriticalRoleRoute>
    ),
  },
  {
    path: 'admin',
    // FLOW[46035]+FLOW[46036]: админ-табы влиты в центр настроек; старые ссылки
    // /v2/admin?tab=… живут через редирект (цель защищена guard'ом центра).
    element: <V2AdminSettingsRedirect />,
  },
  {
    path: V2_FINANCE_ROUTE_PATHS.legalEntities,
    element: <Navigate to="/v2/settings/legal-entities" replace />,
  },
  {
    path: 'admin/users',
    element: <V2AdminUsersRedirect mode="list" />,
  },
  {
    path: 'admin/users/new',
    element: <V2AdminUsersRedirect mode="new" />,
  },
  {
    path: 'admin/users/:id/edit',
    element: <V2AdminUsersRedirect mode="edit" />,
  },
  {
    path: 'executive/communications',
    element: (
      <V2CriticalRoleRoute policy="crmSupervisor">
        {V2ExecutiveCommunicationsPage}
      </V2CriticalRoleRoute>
    ),
  },
  {
    path: 'executive',
    element: (
      <V2CriticalRoleRoute policy="executive">
        {V2ExecutiveDashboardPage}
      </V2CriticalRoleRoute>
    ),
  },
  {
    path: 'notifications',
    element: V2NotificationsPage,
  },
  {
    path: 'profile',
    element: V2ProfilePage,
  },
  {
    path: 'settings',
    // FLOW[47034]: внутренние сотрудники входят ради личной Telegram-секции;
    // реестр по-прежнему скрывает административные настройки по роли.
    element: (
      <ProtectedRoute allowedRoles={SETTINGS_ROUTE_ROLES}>
        {V2SettingsLayout}
      </ProtectedRoute>
    ),
    children: [
      {
        index: true,
        element: V2SettingsIndexRedirect,
      },
      {
        path: ':sectionId',
        element: V2SettingsSectionHost,
      },
    ],
  },
  // ── Прототипы UX-редизайна: только за флагом (dev / VITE_V2_PROTO_ROUTES) ──
  ...(isV2ProtoRoutesEnabled() ? buildV2ProtoRoutes() : []),
];
